import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Save, Trash2, RefreshCw, Star, Tag } from "lucide-react";

interface Plan {
  id?: string;
  name: string;
  original_price_inr: string;
  now_price_inr: string;
  original_price_usd: string;
  now_price_usd: string;
  per_label: string;
  billed_inr: string;
  billed_usd: string;
  sub_label: string;
  offer: string;
  is_best_value: boolean;
  is_active: boolean;
  display_order: number;
}

const emptyPlan: Plan = {
  name: "",
  original_price_inr: "",
  now_price_inr: "",
  original_price_usd: "",
  now_price_usd: "",
  per_label: "/ Month",
  billed_inr: "",
  billed_usd: "",
  sub_label: "Cancel anytime.",
  offer: "",
  is_best_value: false,
  is_active: true,
  display_order: 0,
};

const HomePricingPlansManager = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchPlans = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("home_pricing_plans")
      .select("*")
      .order("display_order", { ascending: true });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setPlans((data as Plan[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const updateLocal = (idx: number, patch: Partial<Plan>) => {
    setPlans((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const savePlan = async (plan: Plan) => {
    setSavingId(plan.id || "new");
    const payload = { ...plan };
    delete (payload as any).id;

    const { error } = plan.id
      ? await supabase.from("home_pricing_plans").update(payload).eq("id", plan.id)
      : await supabase.from("home_pricing_plans").insert(payload);

    setSavingId(null);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Saved", description: `Plan "${plan.name}" saved successfully.` });
      fetchPlans();
    }
  };

  const deletePlan = async (id?: string) => {
    if (!id) {
      setPlans((prev) => prev.filter((p) => p.id));
      return;
    }
    if (!confirm("Delete this plan?")) return;
    const { error } = await supabase.from("home_pricing_plans").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: "Plan removed." });
      fetchPlans();
    }
  };

  const addNew = () => {
    setPlans((prev) => [
      ...prev,
      { ...emptyPlan, display_order: (prev[prev.length - 1]?.display_order || 0) + 1 },
    ]);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tag className="w-5 h-5" />
          Home Page Pricing Plans
        </CardTitle>
        <p className="text-sm text-gray-600">
          Manages the "Pick Your Plan And Get Started Today!" section on the homepage.
          INR fields show for Indian users, USD fields show for everyone else.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {plans.map((plan, idx) => (
          <div
            key={plan.id || `new-${idx}`}
            className="p-4 border rounded-lg space-y-4 bg-gray-50"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">{plan.name || "New Plan"}</h3>
                {plan.is_best_value && (
                  <Badge className="bg-amber-400 text-amber-900">
                    <Star className="w-3 h-3 mr-1" /> Best Value
                  </Badge>
                )}
                <Badge variant={plan.is_active ? "default" : "secondary"}>
                  {plan.is_active ? "Active" : "Hidden"}
                </Badge>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => deletePlan(plan.id)}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <Button
                  size="sm"
                  onClick={() => savePlan(plan)}
                  disabled={savingId === (plan.id || "new")}
                >
                  {savingId === (plan.id || "new") ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Save
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Plan Name</Label>
                <Input
                  value={plan.name}
                  onChange={(e) => updateLocal(idx, { name: e.target.value })}
                  placeholder="Monthly Plan"
                />
              </div>
              <div>
                <Label>Per Label</Label>
                <Input
                  value={plan.per_label}
                  onChange={(e) => updateLocal(idx, { per_label: e.target.value })}
                  placeholder="/ Month"
                />
              </div>
              <div>
                <Label>Offer Badge</Label>
                <Input
                  value={plan.offer}
                  onChange={(e) => updateLocal(idx, { offer: e.target.value })}
                  placeholder="33% OFF"
                />
              </div>
              <div>
                <Label>Sub Label</Label>
                <Input
                  value={plan.sub_label}
                  onChange={(e) => updateLocal(idx, { sub_label: e.target.value })}
                  placeholder="Cancel anytime."
                />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={plan.display_order}
                  onChange={(e) =>
                    updateLocal(idx, { display_order: parseInt(e.target.value || "0") })
                  }
                />
              </div>
              <div className="flex items-center gap-6 pt-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={plan.is_best_value}
                    onCheckedChange={(c) => updateLocal(idx, { is_best_value: c })}
                  />
                  <Label>Best Value</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={plan.is_active}
                    onCheckedChange={(c) => updateLocal(idx, { is_active: c })}
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              {/* INR */}
              <div className="p-3 border rounded-md bg-white space-y-3">
                <h4 className="font-semibold text-indigo-700">🇮🇳 INR (Indian users)</h4>
                <div>
                  <Label>Original Price</Label>
                  <Input
                    value={plan.original_price_inr}
                    onChange={(e) =>
                      updateLocal(idx, { original_price_inr: e.target.value })
                    }
                    placeholder="₹999"
                  />
                </div>
                <div>
                  <Label>Now Price</Label>
                  <Input
                    value={plan.now_price_inr}
                    onChange={(e) => updateLocal(idx, { now_price_inr: e.target.value })}
                    placeholder="₹699"
                  />
                </div>
                <div>
                  <Label>Billed Text</Label>
                  <Input
                    value={plan.billed_inr}
                    onChange={(e) => updateLocal(idx, { billed_inr: e.target.value })}
                    placeholder="Billed ₹4999 every 12 months."
                  />
                </div>
              </div>
              {/* USD */}
              <div className="p-3 border rounded-md bg-white space-y-3">
                <h4 className="font-semibold text-emerald-700">🌎 USD (International)</h4>
                <div>
                  <Label>Original Price</Label>
                  <Input
                    value={plan.original_price_usd}
                    onChange={(e) =>
                      updateLocal(idx, { original_price_usd: e.target.value })
                    }
                    placeholder="$14.99"
                  />
                </div>
                <div>
                  <Label>Now Price</Label>
                  <Input
                    value={plan.now_price_usd}
                    onChange={(e) => updateLocal(idx, { now_price_usd: e.target.value })}
                    placeholder="$9.99"
                  />
                </div>
                <div>
                  <Label>Billed Text</Label>
                  <Input
                    value={plan.billed_usd}
                    onChange={(e) => updateLocal(idx, { billed_usd: e.target.value })}
                    placeholder="Billed $49.99 every 12 months."
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        <Button onClick={addNew} variant="outline" className="w-full">
          <Plus className="w-4 h-4 mr-2" /> Add New Plan
        </Button>
      </CardContent>
    </Card>
  );
};

export default HomePricingPlansManager;
